/* Phase 5 · jp-collection.js — progressive enhancement only.
   Filters/sort are a plain GET form (work with no JS); this auto-submits
   on change (debounced for price inputs) and marks html.js so the
   Apply button hides. P0.5: shopify:section:load re-init (Customiser
   re-renders replace the section DOM and kill bindings). */
(() => {
  if (window.__jpCollectionInit) return;
  window.__jpCollectionInit = true;
  document.documentElement.classList.add('js');

  /* persona size-rec note in the size facet: show the active persona's recommended
     blade length (data-map = {salon,barber,student}). Source of truth is the body
     persona-<id> class set by jp-persona.js; updates on persona:changed. Visual-only —
     never touches the filter inputs. */
  const currentPersona = () => (document.body.className.match(/\bpersona-(all|salon|barber|student)\b/) || [null, 'all'])[1];
  const updateSizeRec = () => {
    const p = currentPersona();
    document.querySelectorAll('[data-jp-sizerec]').forEach((el) => {
      let map = {};
      try { map = JSON.parse(el.dataset.map || '{}'); } catch (e) { /* malformed map → hide */ }
      const range = (p && p !== 'all' && map[p]) ? map[p] : '';
      const out = el.querySelector('[data-jp-sizerec-val]');
      if (range) { if (out) out.textContent = range; el.hidden = false; }
      else { el.hidden = true; }
    });
  };

  const initScope = (scope) => {
    const root = scope || document;

    /* mobile: the filter rail is an off-canvas dialog (jp-layers handles open/close/trap/scrim/
       focus-return via data-jp-open/-close/-layer). Apply dialog semantics ONLY at the mobile
       breakpoint — the desktop sidebar and the no-JS inline panel must not be a dialog; CSS
       visibility:hidden keeps the closed drawer out of the tab order + a11y tree. */
    root.querySelectorAll('.jp-col__rail-panel').forEach((panel) => {
      const mq = window.matchMedia('(max-width: 989px)');
      panel.__jpSetDialog = () => {
        if (mq.matches) { panel.setAttribute('role', 'dialog'); panel.setAttribute('aria-modal', 'true'); }
        else { panel.removeAttribute('role'); panel.removeAttribute('aria-modal'); }
      };
      panel.__jpSetDialog();
      /* COL-1: if the viewport leaves mobile while the drawer is open, close the layer so
         jp-layers releases the body scroll-lock + scrim. Above 989px the panel reverts to a
         static sidebar with no visible close affordance, so a left-open drawer would otherwise
         freeze the page behind the scrim. */
      if (!panel.__jpMqBound) {
        panel.__jpMqBound = true;
        mq.addEventListener('change', (e) => {
          if (!e.matches && panel.classList.contains('is-open') && window.JapanScissors && window.JapanScissors.layers) {
            window.JapanScissors.layers.close(panel.getAttribute('data-jp-layer'));
          }
        });
      }
    });

    root.querySelectorAll('[data-jp-filter-form]').forEach((form) => {
      if (form.dataset.jpBound) return;
      form.dataset.jpBound = 'true';
      let t;
      form.addEventListener('change', (e) => {
        /* in the mobile drawer, batch filter changes — the Apply button submits (no reload per tap) */
        if (e.target.closest('.jp-col__rail-panel') && window.matchMedia('(max-width: 989px)').matches) return;
        if (e.target.matches('input[type="number"]')) {
          window.clearTimeout(t);
          t = setTimeout(() => form.submit(), 900);
          return;
        }
        form.submit();
      });
    });

    /* sticky toolbar: flag .is-stuck once it pins under the header → gap-cover + shadow.
       The header hides on scroll (sticky_type on-scroll-up), so the offset alone can't
       prevent content showing in the gap; the .is-stuck silk-white ::before covers it. */
    root.querySelectorAll('[data-sticky-bar] .jp-col__bar').forEach((bar) => {
      if (bar.dataset.jpStuck) return;
      bar.dataset.jpStuck = 'true';
      const sentinel = document.createElement('div');
      sentinel.className = 'jp-col__bar-sentinel';
      sentinel.setAttribute('aria-hidden', 'true');
      bar.before(sentinel);
      let io;
      bar.__jpReobserve = () => {
        if (io) io.disconnect();
        const hh = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--header-height'), 10) || 0;
        io = new IntersectionObserver(
          (entries) => bar.classList.toggle('is-stuck', !entries[0].isIntersecting),
          { rootMargin: `-${hh + 1}px 0px 0px 0px`, threshold: 0 }
        );
        io.observe(sentinel);
      };
      bar.__jpReobserve();
    });

    /* below-grid SEO description: clamp + read-more (progressive enhancement —
       full text is always in the DOM; we only collapse it when it overflows) */
    root.querySelectorAll('[data-jp-readmore]').forEach((box) => {
      if (box.dataset.jpBound) return;
      box.dataset.jpBound = 'true';
      const btn = box.parentElement.querySelector('[data-jp-readmore-btn]');
      if (!btn) return;
      const labels = btn.querySelector('span');
      const moreTxt = labels ? labels.textContent : 'Read more';
      const lessTxt = btn.dataset.less || moreTxt.replace(/more/i, 'less');
      const measure = () => {
        // only clamp when the content is meaningfully taller than the clamp
        if (box.scrollHeight - box.clientHeight > 24) {
          box.classList.add('is-clamped');
          btn.hidden = false;
        } else {
          box.classList.remove('is-clamped');
          btn.hidden = true;
        }
      };
      box.classList.add('is-clamped');
      measure();
      btn.addEventListener('click', () => {
        const open = box.classList.toggle('is-open');
        box.classList.toggle('is-clamped', !open);
        btn.setAttribute('aria-expanded', open ? 'true' : 'false');
        if (labels) labels.textContent = open ? lessTxt : moreTxt;
      });
      let rt;
      box.__jpRemeasure = () => {
        if (box.classList.contains('is-open')) return;
        window.clearTimeout(rt);
        rt = setTimeout(measure, 200);
      };
    });

    updateSizeRec();
  };

  /* one shared resize listener (added once) re-measures every current read-more
     box — avoids per-box listener accumulation across Customiser re-renders */
  window.addEventListener('resize', () => {
    document.querySelectorAll('[data-jp-readmore]').forEach((b) => { if (b.__jpRemeasure) b.__jpRemeasure(); });
    document.querySelectorAll('[data-sticky-bar] .jp-col__bar').forEach((b) => { if (b.__jpReobserve) b.__jpReobserve(); });
    document.querySelectorAll('.jp-col__rail-panel').forEach((p) => { if (p.__jpSetDialog) p.__jpSetDialog(); });
  }, { passive: true });

  document.addEventListener('persona:changed', updateSizeRec);

  document.addEventListener('DOMContentLoaded', () => initScope());
  document.addEventListener('shopify:section:load', (e) => initScope(e.target));
})();
